"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/components/auth/AuthProvider";
import type { FogPayload, GraphicsQuality, PublicEntity, WaterPayload } from "./EmpireWorld3D";
import { CIVS, MISSIONS, type CivId, type Difficulty, type MissionId } from "./empire-rules";
import { MAP_PICK_LIST, MapPicker } from "./empire-map-preview";
import { getMapById } from "./empire-maps";
import {
  AGE_LABELS,
  BUILD_AGE,
  BUILD_LABELS,
  COST,
  MARKET_GOODS,
  MARKET_GOOD_LABELS,
  MARKET_LOT,
  UNIT_AGE,
  UNIT_LABELS,
  defaultMarketPrices,
  formatCost,
  isFoodAnimal,
  isPredator,
  type Age,
  type BuildKind,
  type MarketGood,
  type MarketPrices,
  type ResType,
  type TechId,
  type TrainUnit,
} from "./empire-game";

const DIVIDE_TASKS: { id: ResType; label: string }[] = [
  { id: "wood", label: "Wood" },
  { id: "food", label: "Food" },
  { id: "gold", label: "Gold" },
  { id: "stone", label: "Stone" },
];
import {
  defaultGraphicsQuality,
  parseGraphicsQuality,
} from "./webgl-graphics";
import {
  loadEmpireMute,
  playAttackHit,
  playAttackOrder,
  playBuildComplete,
  playBuildPlace,
  playChronoPickup,
  playChronoshift,
  playChronoVictim,
  playDefeat,
  playDeposit,
  playDraw,
  playFogDiscover,
  playGatherOrder,
  playGatherTick,
  playMoveAck,
  playSelect,
  playBuildingSelect,
  playVillagerShout,
  playWarriorShout,
  playStop,
  playHeal,
  playResearch,
  playTrade,
  playTrain,
  playTrainDone,
  playUiClick,
  playVictory,
  playAgeUp,
  playWallPlace,
  playUnderAttack,
  setEmpireMuted,
} from "./empire-sounds";

const EmpireWorld3D = dynamic(() => import("./EmpireWorld3D"), {
  ssr: false,
  loading: () => (
    <div className="empire-stage flex items-center justify-center text-[13px] text-[#8aa090]">
      Charting the realm…
    </div>
  ),
});

type ChatMsg = { id: number; name: string; team: string; body: string };

type ChronoCharge = {
  tier: string;
  name: string;
  rewindSec: number;
  relicId: string;
  capturedAt: number;
} | null;

type GameEvent = {
  id: number;
  kind: string;
  team?: string | null;
  text: string;
  rewindSec?: number;
  vaultName?: string;
};

type RoomState = {
  ok?: boolean;
  code: string;
  status: "waiting" | "active" | "finished";
  version: number;
  amber_name: string;
  teal_name?: string;
  your_team: string | null;
  your_role?: string;
  seats?: Array<{
    team: string;
    name: string;
    label: string;
    is_computer: boolean;
    is_you: boolean;
    open: boolean;
  }>;
  seat_count?: number;
  open_computer_seats?: number;
  stake_amount?: number;
  pot?: number;
  pot_escrowed?: boolean;
  pot_settled?: boolean;
  practice?: boolean;
  result?: string | null;
  game?: {
    tick: number;
    started: boolean;
    practice?: boolean;
    winner: string | null;
    eco: Record<string, { wood: number; food: number; gold: number; stone: number }>;
    meta?: Record<
      string,
      {
        age: Age;
        pop: number;
        popCap: number;
        atkBonus: number;
        armorBonus: number;
        wallHpBonus: number;
        rangeBonus: number;
        masonry: boolean;
        ballistics: boolean;
      }
    >;
    entities: PublicEntity[];
    chronoCharge?: ChronoCharge;
    events?: GameEvent[];
    fog?: FogPayload;
    beaches?: string[];
    water?: WaterPayload;
    mapHalf?: number;
    marketPrices?: MarketPrices;
    mapId?: string | null;
    mapName?: string | null;
    attacksAt?: number;
    difficulty?: Difficulty;
    civ?: string | null;
    mission?: string;
    diplomacy?: Record<string, string>;
    mapBiome?: string | null;
    mapSeed?: number | null;
    alliances?: Record<string, number>;
  };
  chat?: ChatMsg[];
  chat_head?: number;
  player_token?: string;
  error?: string;
};

type OpenRoom = {
  code: string;
  host_name: string;
  amber_name: string;
  teal_name?: string;
  state: string;
  humans: number;
  seat_open: boolean;
  seat_count?: number;
  open_computer_seats?: number;
  stake_amount: number;
  practice?: boolean;
};

const LS_KEY = "shafi_empire_session_v1";
const STAKE_PRESETS = [0, 100, 500, 1000] as const;

const LAND_UNITS = new Set([
  "worker", "militia", "spearman", "man_at_arms",
  "archer", "skirmisher", "crossbowman", "hand_cannon",
  "scout_cav", "knight", "camel_rider", "war_elephant",
  "ram", "mangonel", "scorpion", "trebuchet",
  "geologist", "healer", "explorer",
]);
const NAVAL_UNITS = new Set(["fishing_boat", "transport", "war_galley"]);
const ALL_UNITS = new Set([...LAND_UNITS, ...NAVAL_UNITS]);
const VILLAGER_KINDS = new Set(["worker", "geologist", "healer"]);
const WARRIOR_KINDS = new Set([
  "militia", "spearman", "man_at_arms",
  "archer", "skirmisher", "crossbowman", "hand_cannon",
  "scout_cav", "knight", "camel_rider", "war_elephant",
  "ram", "mangonel", "scorpion", "trebuchet",
]);

function shoutForKinds(kinds: string[]) {
  let villagers = 0;
  let warriors = 0;
  for (const kind of kinds) {
    if (VILLAGER_KINDS.has(kind)) villagers += 1;
    else if (WARRIOR_KINDS.has(kind)) warriors += 1;
  }
  if (warriors > villagers) playWarriorShout();
  else if (villagers) playVillagerShout();
  else if (warriors) playWarriorShout();
  else playSelect();
}
const BUILDINGS = new Set([
  "town_hall", "house", "farm", "mill", "lumber_camp", "mining_camp",
  "barracks", "archery_range", "stable", "blacksmith", "university",
  "market", "dock", "watch_tower", "palisade", "wall", "fortified_wall", "gate", "siege_workshop", "keep",
  "church", "granary", "storage_pit", "wonder", "ruin",
]);
/** How far each building's land reaches. Town center holds the base; walls are the border. */
function groundRadius(kind: string): number {
  if (kind === "town_hall" || kind === "keep") return 11;
  if (kind === "watch_tower") return 8;
  if (
    kind === "palisade" ||
    kind === "wall" ||
    kind === "fortified_wall" ||
    kind === "gate"
  )
    return 3.4;
  if (kind === "farm") return 4.5;
  return 6.5;
}

function hostileTeam(
  mine: string | null | undefined,
  other: string | null | undefined,
  alliances?: Record<string, number>
): boolean {
  if (!mine || !other || mine === other) return false;
  const aa = alliances?.[mine];
  const bb = alliances?.[other];
  if (aa != null && bb != null && aa === bb) return false;
  return true;
}

function enemiesOnOurGround(
  team: string,
  ents: PublicEntity[],
  alliances?: Record<string, number>
): Set<string> {
  const sites = ents.filter(
    (e) => e.team === team && e.hp > 0 && BUILDINGS.has(e.kind)
  );
  const ids = new Set<string>();
  if (!sites.length) return ids;
  for (const e of ents) {
    if (e.hp <= 0 || !ALL_UNITS.has(e.kind)) continue;
    if (!hostileTeam(team, e.team, alliances)) continue;
    for (const s of sites) {
      const r = groundRadius(s.kind);
      const dx = e.x - s.x;
      const dz = e.z - s.z;
      if (dx * dx + dz * dz <= r * r) {
        ids.add(e.id);
        break;
      }
    }
  }
  return ids;
}

const RESOURCES = new Set([
  "wood",
  "food",
  "gold",
  "stone",
  "fish",
  "farm",
  "sheep",
  "chicken",
  "cow",
  "goat",
  "duck",
  "deer",
  "oil_seep",
  "gas_seep",
]);

const BUILD_MENU: BuildKind[] = [
  "house", "farm", "mill", "lumber_camp", "mining_camp",
  "barracks", "archery_range", "stable", "blacksmith",
  "market", "dock", "watch_tower", "palisade", "wall", "fortified_wall", "gate",
  "university", "siege_workshop", "keep", "church",
];

const TRAIN_BY_BUILDING: Record<string, TrainUnit[]> = {
  town_hall: ["worker"],
  barracks: ["militia", "spearman", "man_at_arms"],
  archery_range: ["archer", "skirmisher", "crossbowman", "hand_cannon"],
  stable: ["scout_cav", "knight", "camel_rider", "war_elephant"],
  siege_workshop: ["ram", "mangonel", "scorpion", "trebuchet"],
  dock: ["fishing_boat", "transport", "war_galley"],
  university: ["geologist"],
  church: ["healer"],
};

const RESEARCH_BY_BUILDING: Record<string, TechId[]> = {
  town_hall: ["age_feudal", "age_castle", "age_imperial"],
  blacksmith: ["bs_attack", "bs_armor"],
  university: ["uni_masonry", "uni_ballistics"],
};

function AGE_RANK_CLIENT(a: Age): number {
  return a === "imperial" ? 3 : a === "castle" ? 2 : a === "feudal" ? 1 : 0;
}

const TECH_LABELS: Record<TechId, string> = {
  age_feudal: "Advance to Feudal",
  age_castle: "Advance to Castle",
  age_imperial: "Advance to Imperial",
  bs_attack: "+1 Attack",
  bs_armor: "+1 Armor",
  uni_masonry: "Masonry (+wall HP)",
  uni_ballistics: "Ballistics (+range)",
};


function fmtTokens(n: number): string {
  return Math.floor(n || 0).toLocaleString();
}

function minsLabel(sec: number): string {
  return `${Math.round(sec / 60)} min`;
}

function HudIcon({ kind }: { kind: string }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (kind === "food" || kind === "farm" || kind === "mill") {
    return (
      <svg {...common}><path d="M12 21V10M8 14c2-4 8-4 8 0M6 8c3-5 9-5 12 0" /></svg>
    );
  }
  if (kind === "wood" || kind === "lumber_camp" || kind === "palisade") {
    return (
      <svg {...common}><path d="M8 21V9l4-5 4 5v12M7 21h10" /></svg>
    );
  }
  if (kind === "gold" || kind === "market") {
    return (
      <svg {...common}><circle cx="12" cy="12" r="6" /><path d="M12 8v8M9.5 10.5h5M9.5 13.5h5" /></svg>
    );
  }
  if (kind === "stone" || kind === "mining_camp" || kind === "wall" || kind === "fortified_wall") {
    return (
      <svg {...common}><path d="M4 18h16M6 18V9l6-4 6 4v9M9 18v-4h6v4" /></svg>
    );
  }
  if (kind === "gate") {
    return (
      <svg {...common}><path d="M6 20V8h12v12M12 20v-7M9 13h6" /></svg>
    );
  }
  if (kind === "idle") {
    return (
      <svg {...common}><circle cx="9" cy="8" r="2" /><circle cx="15" cy="8" r="2" /><path d="M5 19c.6-3 2.4-4 4-4s3.4 1 4 4M11 19c.6-3 2.4-4 4-4s3.4 1 4 4" /></svg>
    );
  }
  if (kind === "house") {
    return (
      <svg {...common}><path d="M4 11l8-7 8 7v9H4zM10 20v-5h4v5" /></svg>
    );
  }
  if (kind === "barracks" || kind === "keep" || kind === "watch_tower") {
    return (
      <svg {...common}><path d="M8 21V4l8 3v14M8 10h8M8 14h8" /></svg>
    );
  }
  if (kind === "archery_range") {
    return (
      <svg {...common}><path d="M4 16c4-8 12-8 16 0M12 16V6M10 8l2-3 2 3" /></svg>
    );
  }
  if (kind === "stable") {
    return (
      <svg {...common}><path d="M5 18l2-8 4 2 2-4 4 3 2 7M6 18h12" /></svg>
    );
  }
  if (kind === "dock") {
    return (
      <svg {...common}><path d="M3 16h18M7 16V8h10v8M12 8V4" /></svg>
    );
  }
  if (kind === "university" || kind === "blacksmith") {
    return (
      <svg {...common}><path d="M3 10l9-5 9 5-9 5zM7 12v5c2 2 8 2 10 0v-5" /></svg>
    );
  }
  return (
    <svg {...common}><path d="M5 19V8l7-4 7 4v11H5z" /></svg>
  );
}

export default function EmpireClient() {
  const { user } = useAuth();
  const search = useSearchParams();
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [lobbyMsg, setLobbyMsg] = useState("");
  const [mapId, setMapId] = useState(MAP_PICK_LIST[0]?.id || "");
  const [difficulty, setDifficulty] = useState<Difficulty>("normal");
  const [civ, setCiv] = useState<CivId>("assyria");
  const [mission, setMission] = useState<MissionId>("skirmish");
  const [attackGround, setAttackGround] = useState(false);
  const [diplomacyOpen, setDiplomacyOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [myTokens, setMyTokens] = useState(0);
  const [stakePreset, setStakePreset] = useState(0);
  const [stakeCustom, setStakeCustom] = useState("");
  const [acceptStake, setAcceptStake] = useState(true);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [token, setToken] = useState("");
  const [openRooms, setOpenRooms] = useState<OpenRoom[]>([]);
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [cmdMsg, setCmdMsg] = useState("");
  const [buildMode, setBuildMode] = useState<BuildKind | null>(null);
  const [toast, setToast] = useState("");
  const [panSpeed, setPanSpeed] = useState(1);
  const [quality, setQuality] = useState<GraphicsQuality>("high");
  const [preferHighPerfGpu, setPreferHighPerfGpu] = useState(true);
  const [sfxMuted, setSfxMuted] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  /** Tasks toggled for Divide Work (even split across selected villagers). */
  const [divideTasks, setDivideTasks] = useState<ResType[]>([
    "wood",
    "food",
    "gold",
    "stone",
  ]);
  /** How many selected villagers to include (2–12 chips, or all). */
  const [dividePeople, setDividePeople] = useState<number | "all">("all");
  const [attackMove, setAttackMove] = useState(false);
  const [patrolArmed, setPatrolArmed] = useState(false);
  const [lookAt, setLookAt] = useState<{ x: number; z: number; n: number } | null>(null);
  const lookN = useRef(0);
  const idleCursor = useRef(0);
  const queueingRef = useRef(false);
  const [buildTab, setBuildTab] = useState<"military" | "economy" | "walls">("economy");
  const groupsRef = useRef<Record<number, string[]>>({});
  const aDownRef = useRef(0);


  const versionRef = useRef(0);
  const mapPickRef = useRef(MAP_PICK_LIST[0]?.id || "");
  const mapShownRef = useRef("");
  const waterKeepRef = useRef<WaterPayload | null>(null);
  const propsKeepRef = useRef<PublicEntity[]>([]);
  const fogKeepRef = useRef<FogPayload | null>(null);
  const tickShownRef = useRef(0);
  const roomGen = useRef(0);
  const chatHeadRef = useRef(0);
  const tokenRef = useRef("");
  const codeRef = useRef("");
  const applyRef = useRef<(data: RoomState, gen?: number) => void>(() => {});
  const seenEvents = useRef(new Set<number>());
  const chatListRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const selectedRef = useRef<string[]>([]);
  const prevEntitiesRef = useRef<PublicEntity[]>([]);
  /** Lifetime unit ids — avoids trainDone "tudu" when poll re-lists known villagers. */
  const knownUnitIdsRef = useRef<Set<string>>(new Set());
  const prevEcoRef = useRef<{ wood: number; food: number; gold: number; stone: number } | null>(null);
  const prevFogCountRef = useRef(0);
  const winnerSoundedRef = useRef(false);
  /** Enemy unit ids already standing on our ground — a new id is an invasion. */
  const enemiesOnGroundRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
        e.preventDefault();
        const btn = document.getElementById("empire-select-villagers");
        if (btn) btn.click();
      }
      if (!e.ctrlKey && !e.metaKey && e.key.toLowerCase() === "a") {
        aDownRef.current = performance.now();
      }
      if ((e.ctrlKey || e.metaKey) && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        groupsRef.current[Number(e.key)] = [...selectedRef.current];
        setCmdMsg(`Group ${e.key} saved`);
      } else if (!e.ctrlKey && !e.metaKey && !e.altKey && /^[1-9]$/.test(e.key)) {
        const ids = groupsRef.current[Number(e.key)] || [];
        if (ids.length) setSelected(ids);
      }
      if (e.key === "." || e.key === ">") {
        e.preventDefault();
        const btn = document.getElementById("empire-divide-work");
        if (btn) btn.click();
      }
      if (e.key === "," || e.key === "<") {
        e.preventDefault();
        document.getElementById("empire-idle-villager")?.click();
      }
      if (!e.ctrlKey && !e.metaKey && e.key.toLowerCase() === "h") {
        e.preventDefault();
        document.getElementById("empire-town-center")?.click();
      }
      if (e.key === " ") {
        e.preventDefault();
        document.getElementById("empire-center-selection")?.click();
      }
      if (e.key === "Escape") {
        setBuildMode(null);
        setAttackMove(false);
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const fort = document.getElementById("empire-raze-fort");
        if (fort && !(fort as HTMLButtonElement).disabled) {
          e.preventDefault();
          fort.click();
        } else {
          document.getElementById("empire-delete-unit")?.click();
        }
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "a" || e.ctrlKey || e.metaKey) return;
      if (performance.now() - aDownRef.current < 240) setAttackMove(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onUp);
    };
  }, []);

  useEffect(() => {
    const muted = loadEmpireMute();
    setSfxMuted(muted);
    setEmpireMuted(muted);
    try {
      const raw =
        localStorage.getItem("x_empire_settings_v2") ||
        localStorage.getItem("x_empire_settings_v1");
      if (!raw) {
        setQuality(defaultGraphicsQuality());
        return;
      }
      const s = JSON.parse(raw) as {
        panSpeed?: number;
        quality?: string;
        preferHighPerfGpu?: boolean;
        sfxMuted?: boolean;
      };
      if (typeof s.panSpeed === "number") setPanSpeed(s.panSpeed);
      if (s.quality) setQuality(parseGraphicsQuality(s.quality, defaultGraphicsQuality()));
      else setQuality(defaultGraphicsQuality());
      if (typeof s.preferHighPerfGpu === "boolean") {
        setPreferHighPerfGpu(s.preferHighPerfGpu);
      }
      if (typeof s.sfxMuted === "boolean") {
        setSfxMuted(s.sfxMuted);
        setEmpireMuted(s.sfxMuted);
      }
    } catch {
      setQuality(defaultGraphicsQuality());
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        "x_empire_settings_v2",
        JSON.stringify({ panSpeed, quality, preferHighPerfGpu, sfxMuted })
      );
    } catch {
      /* ignore */
    }
  }, [panSpeed, quality, preferHighPerfGpu, sfxMuted]);

  useEffect(() => {
    setEmpireMuted(sfxMuted);
  }, [sfxMuted]);

  useEffect(() => {
    const sync = () => {
      const el = shellRef.current;
      const fsEl =
        document.fullscreenElement ||
        (document as Document & { webkitFullscreenElement?: Element })
          .webkitFullscreenElement;
      setIsFullscreen(!!el && fsEl === el);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = shellRef.current;
    if (!el) return;
    try {
      const doc = document as Document & {
        webkitExitFullscreen?: () => Promise<void> | void;
        webkitFullscreenElement?: Element;
      };
      const node = el as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void> | void;
      };
      const fsEl = document.fullscreenElement || doc.webkitFullscreenElement;
      if (fsEl === el) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (doc.webkitExitFullscreen) await doc.webkitExitFullscreen();
      } else if (node.requestFullscreen) await node.requestFullscreen();
      else if (node.webkitRequestFullscreen) await node.webkitRequestFullscreen();
    } catch {
      setCmdMsg("Fullscreen not available");
    }
  }, []);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    codeRef.current = room?.code || "";
    versionRef.current = room?.version || 0;
  }, [room]);
  useEffect(() => {
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      if (saved?.name) setName(saved.name);
    } catch {
      /* ignore */
    }
    const q = (search.get("room") || search.get("code") || "").toUpperCase();
    if (q) setJoinCode(q);
  }, [search]);

  useEffect(() => {
    if (user && !name) setName(user.display_name || user.handle || "");
  }, [user, name]);

  useEffect(() => {
    let stop = false;
    const loadTokens = async () => {
      try {
        const res = await fetch("/api/empire/health", {
          credentials: "include",
          cache: "no-store",
        });
        const data = await res.json().catch(() => ({}));
        if (!stop && typeof data.game_tokens === "number") setMyTokens(data.game_tokens);
      } catch {
        /* ignore */
      }
    };
    void loadTokens();
    const t = window.setInterval(loadTokens, room ? 8000 : 20000);
    return () => {
      stop = true;
      window.clearInterval(t);
    };
  }, [user?.id, room?.code]);

  function selectedStake(): number {
    const custom = Math.floor(Number(stakeCustom) || 0);
    if (stakeCustom.trim() !== "" && Number.isFinite(custom) && custom > 0) {
      return Math.min(custom, 100_000);
    }
    return stakePreset;
  }

  const save = useCallback((code: string, tok: string, n: string) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ code, token: tok, name: n }));
    } catch {
      /* ignore */
    }
  }, []);

  const flashToast = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(""), 4200);
  }, []);

  const apply = useCallback(
    (data: RoomState, gen?: number) => {
      if (gen != null && gen !== roomGen.current) return;
      if (!data?.code) return;
      if (codeRef.current && data.code !== codeRef.current && gen == null) return;
      const team = data.your_team;
      const ents = data.game?.entities || [];
      const prev = prevEntitiesRef.current;
      const winnerNow =
        data.game?.winner || (data.status === "finished" ? data.result : null);
      // Never replay gather/fog/combat stingers on ended matches or lobby polls
      const sfxLive =
        !!team &&
        data.status === "active" &&
        !!data.game?.started &&
        !winnerNow;
      if (!prev.length) {
        knownUnitIdsRef.current.clear();
        for (const e of ents) {
          if (e.team === team && ALL_UNITS.has(e.kind)) {
            knownUnitIdsRef.current.add(e.id);
          }
        }
      }
      if (prev.length && sfxLive) {
        const prevById = new Map(prev.map((e) => [e.id, e]));
        let gatherBump = false;
        let gatherKind = "";
        let hit = false;
        let buildDone = false;
        let trainDone = false;
        for (const e of ents) {
          if (e.team !== team) continue;
          const p = prevById.get(e.id);
          if (!p) {
            // Only brand-new trained units — not fog/poll reappearances
            if (
              ALL_UNITS.has(e.kind) &&
              !knownUnitIdsRef.current.has(e.id)
            ) {
              trainDone = true;
              knownUnitIdsRef.current.add(e.id);
            }
            continue;
          }
          knownUnitIdsRef.current.add(e.id);
          if (
            e.kind === "worker" &&
            (e.carry?.amount || 0) > (p.carry?.amount || 0)
          ) {
            gatherBump = true;
            gatherKind = e.carry?.type || gatherKind;
          }
          if (p.constructing && !e.constructing && BUILDINGS.has(e.kind)) {
            buildDone = true;
          }
          if (p.train && !e.train) trainDone = true;
        }
        for (const e of ents) {
          const p = prevById.get(e.id);
          if (p && e.hp < p.hp && e.team && e.team !== team) hit = true;
          if (p && e.hp < p.hp && e.team === team) hit = true;
        }
        if (gatherBump) playGatherTick(gatherKind);
        if (hit) playAttackHit();
        if (buildDone) playBuildComplete();
        if (trainDone) playTrainDone();
        const eco = team ? data.game?.eco?.[team] : null;
        const prevEco = prevEcoRef.current;
        if (eco && prevEco) {
          if (
            eco.wood > prevEco.wood ||
            eco.food > prevEco.food ||
            (eco.gold || 0) > (prevEco.gold || 0) ||
            (eco.stone || 0) > (prevEco.stone || 0)
          )
            playDeposit();
        }
        if (eco)
          prevEcoRef.current = {
            wood: eco.wood,
            food: eco.food,
            gold: eco.gold || 0,
            stone: eco.stone || 0,
          };
        // Fog ping only on real NEW exploration (monotonic; ignore poll flicker to 0)
        const fogN = data.game?.fog?.explored?.length || 0;
        if (
          prevFogCountRef.current > 0 &&
          fogN > prevFogCountRef.current + 6
        ) {
          playFogDiscover();
        }
        if (fogN > prevFogCountRef.current) prevFogCountRef.current = fogN;
      } else if (team && data.game?.eco?.[team]) {
        const eco = data.game.eco[team];
        prevEcoRef.current = {
          wood: eco.wood,
          food: eco.food,
          gold: eco.gold || 0,
          stone: eco.stone || 0,
        };
        const fogN = data.game?.fog?.explored?.length || 0;
        if (fogN > prevFogCountRef.current) prevFogCountRef.current = fogN;
      }
      prevEntitiesRef.current = ents;

      const winner = winnerNow;
      if (winner && !winnerSoundedRef.current) {
        winnerSoundedRef.current = true;
        if (winner === "draw") playDraw();
        else if (winner === team) playVictory();
        else if (team) playDefeat();
      }
      if (!winner) winnerSoundedRef.current = false;

      if (team && sfxLive) {
        const groundNow = enemiesOnOurGround(team, ents, data.game?.alliances);
        if (prev.length) {
          let invaded = false;
          for (const id of groundNow) {
            if (!enemiesOnGroundRef.current.has(id)) {
              invaded = true;
              break;
            }
          }
          if (invaded && playUnderAttack()) flashToast("Enemy on our ground!");
        }
        enemiesOnGroundRef.current = groundNow;
      }

      const incomingMap = data.game?.mapId || "";
      const incomingTick = data.game?.tick || 0;
      const newerVersion =
        typeof data.version === "number" && data.version > versionRef.current;
      const sameRoom = !!(data.code && codeRef.current && data.code === codeRef.current);
      if (
        sameRoom &&
        incomingMap &&
        mapShownRef.current &&
        incomingMap !== mapShownRef.current &&
        !newerVersion
      ) {
        return;
      }
      if (incomingMap) mapShownRef.current = incomingMap;
      tickShownRef.current = incomingTick;
      const waterCells = data.game?.water?.cells;
      if (waterCells && waterCells.length) waterKeepRef.current = data.game.water;
      else if (waterKeepRef.current && data.game) {
        data.game = { ...data.game, water: waterKeepRef.current };
      }
      if (data.game?.entities) {
        const staticNow = data.game.entities.filter((e) =>
          e.kind === "wood" || e.kind === "food" || e.kind === "gold" || e.kind === "stone" || e.kind === "fish" || e.kind === "beach_kid"
        );
        if (staticNow.length) propsKeepRef.current = staticNow;
        else if (propsKeepRef.current.length) {
          data.game = { ...data.game, entities: data.game.entities.concat(propsKeepRef.current) };
        }
      }
      if (data.game?.fog?.explored?.length) fogKeepRef.current = data.game.fog;
      else if (fogKeepRef.current && data.game) {
        data.game = { ...data.game, fog: fogKeepRef.current };
      }
      setRoom(data);
      versionRef.current = data.version || 0;
      if (typeof data.chat_head === "number") chatHeadRef.current = data.chat_head;
      if (Array.isArray(data.chat)) setChat(data.chat);
      if (data.player_token) {
        setToken(data.player_token);
        tokenRef.current = data.player_token;
      }
      const events = data.game?.events || [];
      for (const ev of events) {
        if (seenEvents.current.has(ev.id)) continue;
        seenEvents.current.add(ev.id);
        if (ev.kind === "chrono_found" && ev.team === data.your_team) {
          playChronoPickup();
          flashToast(
            `${ev.vaultName || "Chrono Vault"} (${minsLabel(ev.rewindSec || 60)}) — ready to chronoshift!`
          );
        } else if (ev.kind === "chrono_victim" && ev.team === data.your_team) {
          playChronoVictim();
          flashToast("You were chronoshifted!");
        } else if (ev.kind === "chrono_used" && ev.team === data.your_team) {
          playChronoshift();
          flashToast(ev.text);
        } else if (ev.kind === "age_up" && ev.team === data.your_team) {
          playAgeUp();
          flashToast(ev.text);
        }
      }
    },
    [flashToast]
  );

  useEffect(() => {
    applyRef.current = apply;
  }, [apply]);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const res = await fetch("/api/empire/rooms", { cache: "no-store" });
        const data = await res.json();
        if (!stop && Array.isArray(data.rooms)) setOpenRooms(data.rooms);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = window.setInterval(tick, 4000);
    return () => {
      stop = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!room?.code || !token) return;
    let stop = false;
    const gen = roomGen.current;
    const loop = async () => {
      while (!stop) {
        try {
          const res = await fetch(
            `/api/empire/rooms/${encodeURIComponent(codeRef.current)}/poll?since=${versionRef.current}&chat_since=${chatHeadRef.current}&token=${encodeURIComponent(tokenRef.current)}`,
            { cache: "no-store" }
          );
          const data = await res.json();
          if (stop) break;
          if (gen === roomGen.current && data?.ok !== false && data?.code) applyRef.current(data, gen);
          await new Promise((r) => setTimeout(r, 160));
        } catch {
          await new Promise((r) => setTimeout(r, 800));
        }
      }
    };
    void loop();
    return () => {
      stop = true;
    };
  }, [room?.code, token]);

  function chooseMap(id: string) {
    mapPickRef.current = id;
    setMapId(id);
  }

  async function create(practice = false) {
    setLobbyMsg("");
    const n = name.trim();
    if (n.length < 2) {
      setLobbyMsg("Enter a display name");
      setCmdMsg("Enter a display name");
      return;
    }
    const stake = 0;
    const picked = getMapById(mapPickRef.current || mapId) || MAP_PICK_LIST[0];
    try {
      const res = await fetch("/api/empire/rooms", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: n,
          stake,
          practice,
          seats: practice ? 2 : 8,
          mapId: picked?.id || mapId,
          mapSeed: picked?.seed,
          mapBiome: picked?.biome,
          difficulty: mission === "first-hearth" ? "easy" : mission === "high-ground" ? "hard" : difficulty,
          civ,
          mission,
          winRule: "standard",
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Create failed");
      seenEvents.current = new Set();
      prevEntitiesRef.current = [];
      enemiesOnGroundRef.current = new Set();
      prevEcoRef.current = null;
      prevFogCountRef.current = 0;
      winnerSoundedRef.current = false;
      roomGen.current += 1;
      codeRef.current = data.code;
      versionRef.current = data.version || 0;
      apply(data, roomGen.current);
      setToken(data.player_token);
      save(data.code, data.player_token, n);
      window.history.replaceState({}, "", `/games/empire?room=${data.code}`);
      setMapOpen(false);
      if (practice) flashToast(data.game?.mapName || picked?.name || "Practice skirmish");
    } catch (e) {
      setLobbyMsg(e instanceof Error ? e.message : "Create failed");
    }
  }

  async function startPractice() {
    if (!room || !token) return;
    setCmdMsg("");
    const picked = getMapById(mapId) || MAP_PICK_LIST[0];
    // Non-host (teal) in a dead room: spin up a fresh solo practice instead
    if (room.your_team !== "amber") {
      await create(true);
      return;
    }
    try {
      const res = await fetch(
        `/api/empire/rooms/${encodeURIComponent(room.code)}/practice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            mapId: picked?.id || mapId,
            mapSeed: picked?.seed,
            mapBiome: picked?.biome,
            difficulty: mission === "first-hearth" ? "easy" : mission === "high-ground" ? "hard" : difficulty,
            civ,
            mission,
            winRule: "standard",
          }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Failed");
      seenEvents.current = new Set();
      prevEntitiesRef.current = [];
      enemiesOnGroundRef.current = new Set();
      prevEcoRef.current = null;
      prevFogCountRef.current = 0;
      winnerSoundedRef.current = false;
      setSelected([]);
      setBuildMode(null);
      roomGen.current += 1;
      codeRef.current = data.code;
      versionRef.current = data.version || 0;
      apply(data, roomGen.current);
      setMapOpen(false);
      flashToast(
        data.restarted
          ? `Playing ${data.game?.mapName || picked?.name || "this map"}`
          : "Practice started"
      );
    } catch (e) {
      setCmdMsg(e instanceof Error ? e.message : "Practice failed");
    }
  }

  async function join(code: string) {
    setLobbyMsg("");
    const n = name.trim();
    const c = code.trim().toUpperCase();
    if (n.length < 2) {
      setLobbyMsg("Enter a display name");
      return;
    }
    if (!c) {
      setLobbyMsg("Enter a room code");
      return;
    }
    try {
      const res = await fetch(`/api/empire/rooms/${encodeURIComponent(c)}/join`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: n,
          token: token || undefined,
          accept_stake: acceptStake,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Join failed");
      seenEvents.current = new Set();
      prevEntitiesRef.current = [];
      enemiesOnGroundRef.current = new Set();
      prevEcoRef.current = null;
      prevFogCountRef.current = 0;
      winnerSoundedRef.current = false;
      roomGen.current += 1;
      codeRef.current = data.code;
      versionRef.current = data.version || 0;
      apply(data, roomGen.current);
      setToken(data.player_token);
      save(data.code, data.player_token, n);
      window.history.replaceState({}, "", `/games/empire?room=${data.code}`);
    } catch (e) {
      setLobbyMsg(e instanceof Error ? e.message : "Join failed");
    }
  }

  async function leave() {
    if (room && token) {
      try {
        await fetch(`/api/empire/rooms/${encodeURIComponent(room.code)}/leave`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
      } catch {
        /* ignore */
      }
    }
    setRoom(null);
    setToken("");
    setSelected([]);
    setChat([]);
    setBuildMode(null);
    prevEntitiesRef.current = [];
    enemiesOnGroundRef.current = new Set();
    prevEcoRef.current = null;
    prevFogCountRef.current = 0;
    winnerSoundedRef.current = false;
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
    window.history.replaceState({}, "", "/games/empire");
  }

  async function sendCmd(command: Record<string, unknown>): Promise<boolean> {
    if (!room || !token) return false;
    if (room.status !== "active") {
      setCmdMsg("Match not active — tap Play Practice / Restart to take control");
      return false;
    }
    setCmdMsg("");
    const ids = [
      ...(Array.isArray(command.unitIds) ? (command.unitIds as string[]) : []),
      ...(typeof command.unitId === "string" ? [command.unitId] : []),
    ];
    if (
      (command.type === "move" || command.type === "attack_move") &&
      typeof command.x === "number" &&
      typeof command.z === "number"
    ) {
      const idSet = new Set(ids);
      const ox = command.x as number;
      const oz = command.z as number;
      const order = command.type as string;
      setRoom((prev) => {
        if (!prev?.game?.entities) return prev;
        return {
          ...prev,
          game: {
            ...prev.game,
            entities: prev.game.entities.map((e) =>
              idSet.has(e.id) ? { ...e, order, ox, oz } : e
            ),
          },
        };
      });
    }
    try {
      let res: Response | null = null;
      let raw = "";
      for (let attempt = 0; attempt < 3; attempt++) {
        res = await fetch(
          `/api/empire/rooms/${encodeURIComponent(room.code)}/command`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token, command }),
          }
        );
        raw = await res.text();
        if (res.status !== 503) break;
        await new Promise((r) => setTimeout(r, 280));
      }
      let data: { ok?: boolean; error?: string } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        setCmdMsg(
          res?.status === 503
            ? "Server is busy — click the map again."
            : "Command failed"
        );
        return false;
      }
      if (!res.ok || data.ok === false) {
        setCmdMsg(data.error || "Command failed");
        return false;
      }
      const t = command.type;
      const kinds = ids
        .map((id) => room.game?.entities.find((e) => e.id === id)?.kind || "")
        .filter(Boolean);
      const who: "villager" | "warrior" = kinds.some((k) => WARRIOR_KINDS.has(k)) && !kinds.some((k) => VILLAGER_KINDS.has(k))
        ? "warrior"
        : "villager";
      const resourceKind = typeof command.resourceId === "string"
        ? room.game?.entities.find((e) => e.id === command.resourceId)?.kind
        : undefined;
      if (t === "move" || t === "board" || t === "unload") playMoveAck(who);
      else if (t === "gather" || t === "divide_work") playGatherOrder(resourceKind);
      else if (t === "attack" || t === "attack_move") playAttackOrder(who);
      else if (t === "stop") playStop(who);
      else if (t === "heal") playHeal();
      else if (t === "research") playResearch();
      else if (t === "market_buy" || t === "market_sell" || t === "market_swap" || t === "trade_route") playTrade();
      else if (t === "build") {
        const b = (command as { building?: string }).building;
        if (
          b === "wall" ||
          b === "gate" ||
          b === "palisade" ||
          b === "fortified_wall"
        )
          playWallPlace();
        else {
          playBuildPlace();
          setBuildMode(null);
          setCmdMsg("Villagers are building.");
        }
      }
      else if (t === "train") playTrain();
      else if (t === "capture") playMoveAck();
      else if (t === "rewind") playChronoshift();
      apply(data);
      return true;
    } catch (e) {
      setCmdMsg(e instanceof Error ? e.message : "Command failed");
      return false;
    }
  }

  function selectAllVillagers() {
    const ids = (room?.game?.entities || [])
      .filter((e) => e.team === room?.your_team && e.kind === "worker" && e.hp > 0)
      .map((e) => e.id);
    setSelected(ids);
    if (ids.length) playVillagerShout();
    else setCmdMsg("No villagers");
  }

  function selectAllUnits() {
    const ids = (room?.game?.entities || [])
      .filter((e) => e.team === room?.your_team && ALL_UNITS.has(e.kind) && e.hp > 0)
      .map((e) => e.id);
    setSelected(ids);
    if (ids.length) {
      shoutForKinds(
        (room?.game?.entities || [])
          .filter((e) => ids.includes(e.id))
          .map((e) => e.kind)
      );
    }
  }

  function toggleDivideTask(t: ResType) {
    setDivideTasks((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  }

  function runDivideWork() {
    if (!divideTasks.length) {
      setCmdMsg("Pick at least one task");
      return;
    }
    const selectedWorkers = selectedRef.current.filter((id) => {
      const e = room?.game?.entities.find((en) => en.id === id);
      return e?.kind === "worker" && e.team === room?.your_team && e.hp > 0;
    });
    let ids =
      selectedWorkers.length > 0
        ? selectedWorkers
        : (room?.game?.entities || [])
            .filter((e) => e.team === room?.your_team && e.kind === "worker" && e.hp > 0)
            .map((e) => e.id);
    if (!ids.length) {
      setCmdMsg("No villagers");
      return;
    }
    // Cap to chosen people count (2–12…) when not "all"
    const people =
      dividePeople === "all" ? ids.length : Math.min(ids.length, dividePeople);
    ids = ids.slice(0, Math.max(1, people));
    setSelected(ids);
    setCmdMsg(`Dividing ${ids.length} villager${ids.length === 1 ? "" : "s"} across ${divideTasks.length} task${divideTasks.length === 1 ? "" : "s"}`);
    void sendCmd({
      type: "divide_work",
      unitIds: ids,
      tasks: divideTasks,
      people: ids.length,
    });
  }

  function myUnits(): string[] {
    return selectedRef.current.filter((id) => {
      const e = room?.game?.entities.find((en) => en.id === id);
      return e && ALL_UNITS.has(e.kind) && e.team === room?.your_team;
    });
  }

  const FORT_KINDS = new Set(["palisade", "wall", "fortified_wall", "gate"]);

  async function razeFort(targetId: string, nearby = false) {
    if (!room?.your_team) return;
    const ent = room.game?.entities.find((e) => e.id === targetId);
    if (!ent || ent.team !== room.your_team) {
      setCmdMsg("Select your gate or wall");
      return;
    }
    if (!nearby && !FORT_KINDS.has(ent.kind)) {
      setCmdMsg("Select your gate or wall");
      return;
    }
    const n = nearby
      ? (room.game?.entities || []).filter((e) => {
          if (e.kind !== "gate" || e.team !== room.your_team || e.hp <= 0) return false;
          const dx = e.x - ent.x;
          const dz = e.z - ent.z;
          return dx * dx + dz * dz <= 64;
        }).length
      : 1;
    if (nearby && n < 1) {
      setCmdMsg("No gates here");
      return;
    }
    const ok = await sendCmd({ type: "destroy_fort", targetId, nearby });
    if (!ok) return;
    setSelected((prev) => prev.filter((x) => x !== targetId));
    setCmdMsg(
      nearby
        ? `Deleted ${n} gate${n === 1 ? "" : "s"}`
        : ent.kind === "gate"
          ? "Deleted gate"
          : "Deleted wall"
    );
  }

  function onSelect(id: string, shift: boolean, alt?: boolean) {
    const ent = room?.game?.entities.find((e) => e.id === id);
    if (!ent) return;
    const myTeam = room?.your_team;

    // Alt+click own wall/gate → raze segment
    if (alt && myTeam && ent.team === myTeam && FORT_KINDS.has(ent.kind)) {
      razeFort(id);
      return;
    }

    if (myTeam && ent.team === myTeam && ent.constructing && BUILDINGS.has(ent.kind)) {
      const workers = selectedWorkers();
      if (workers.length || buildMode) {
        void sendCmd({
          type: "build",
          unitId: workers[0] || "",
          unitIds: workers,
          building: ent.kind,
          x: ent.x,
          z: ent.z,
        });
        return;
      }
    }

    // Select villager(s) → left-click tree / gold / food / farm / livestock → gather
    if ((RESOURCES.has(ent.kind) || isFoodAnimal(ent.kind)) && myTeam) {
      const gatherers = selectedRef.current.filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        if (!u || u.team !== myTeam || u.hp <= 0) return false;
        if (ent.kind === "fish") return u.kind === "fishing_boat";
        if (ent.kind === "oil_seep" || ent.kind === "gas_seep")
          return u.kind === "worker" || u.kind === "geologist";
        return u.kind === "worker";
      });
      if (gatherers.length > 0) {
        void sendCmd({ type: "gather", unitIds: gatherers, resourceId: id });
        return;
      }
    }

    // Chrono relic: left-click with units selected → capture
    if (ent.kind === "chrono" && myTeam) {
      const units = myUnits();
      if (units.length > 0) {
        void sendCmd({ type: "capture", unitIds: units, relicId: id });
        return;
      }
    }

    // Enemy or predator: left-click with military/workers → attack
    if (
      myTeam &&
      ((ent.team && ent.team !== myTeam) || isPredator(ent.kind))
    ) {
      const units = myUnits().filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        return !!u && WARRIOR_KINDS.has(u.kind);
      });
      const peace = (room?.game?.attacksAt || 0) > Date.now();
      if (units.length > 0 && !peace) {
        void sendCmd({ type: "attack", unitIds: units, targetId: id });
        return;
      }
      if (peace) setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
      else if (myUnits().length) {
        setCmdMsg(
          ent.kind === "town_hall"
            ? "Villagers do not attack towns. Only warriors do, after the peace."
            : "Only warriors attack."
        );
      }
    }

    if (ent.team === myTeam && ALL_UNITS.has(ent.kind)) {
      setSelected((prev) => {
        if (shift) {
          return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        }
        return [id];
      });
      setBuildMode(null);
      shoutForKinds([ent.kind]);
      return;
    }
    if (ent.team === myTeam && BUILDINGS.has(ent.kind)) {
      // Own farm with villagers selected → harvest (farm is also a building)
      if (ent.kind === "farm") {
        const gatherers = selectedRef.current.filter((uid) => {
          const u = room?.game?.entities.find((en) => en.id === uid);
          return u?.kind === "worker" && u.team === myTeam && u.hp > 0;
        });
        if (gatherers.length > 0) {
          void sendCmd({ type: "gather", unitIds: gatherers, resourceId: id });
          return;
        }
      }
      setSelected([id]);
      setBuildMode(null);
      playBuildingSelect();
    }
  }

  function onBoxSelect(ids: string[], additive = false) {
    // No artificial cap — keep every id the drag rectangle caught (2…N villagers).
    setSelected((prev) => {
      if (!additive) return ids;
      const set = new Set(prev);
      for (const id of ids) set.add(id);
      return Array.from(set);
    });
    setBuildMode(null);
    if (ids.length) {
      shoutForKinds(
        (room?.game?.entities || [])
          .filter((e) => ids.includes(e.id))
          .map((e) => e.kind)
      );
    }
  }

  function selectedWorkers(): string[] {
    return selectedRef.current.filter((id) => {
      const e = room?.game?.entities.find((en) => en.id === id);
      return e?.kind === "worker" && e.team === room?.your_team && e.hp > 0;
    });
  }

  function nearestGatherNode(kinds: string[], unitIds: string[]) {
    const ents = room?.game?.entities || [];
    const who = ents.filter((e) => unitIds.includes(e.id));
    const nodes = ents.filter((e) => {
      if (!kinds.includes(e.kind) || e.hp <= 0 || e.constructing) return false;
      if (e.kind === "farm") return true;
      return (e.stock ?? 0) > 0;
    });
    if (!nodes.length || !who.length) return null;
    let best = nodes[0];
    let bestD = Infinity;
    for (const n of nodes) {
      let d = 0;
      for (const w of who) d += Math.hypot(n.x - w.x, n.z - w.z);
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }

  /** Classic RTS right-click on entity. */
  function onEntityRightClick(id: string, shift?: boolean, alt?: boolean) {
    const ent = room?.game?.entities.find((e) => e.id === id);
    if (!ent || !room?.your_team) return;
    const units = myUnits();
    if (
      alt &&
      ent.team === room.your_team &&
      (ent.kind === "town_hall" || ent.kind === "watch_tower" || ent.kind === "keep") &&
      units.length
    ) {
      void sendCmd({ type: "garrison", unitIds: units, buildingId: id });
      setCmdMsg("Inside");
      return;
    }
    if (units.length === 0 && !(ent.team === room.your_team && ent.constructing)) return;
    if (
      ent.team === room.your_team &&
      ent.constructing &&
      BUILDINGS.has(ent.kind)
    ) {
      const workers = selectedWorkers();
      void sendCmd({
        type: "build",
        unitId: workers[0] || "",
        unitIds: workers,
        building: ent.kind,
        x: ent.x,
        z: ent.z,
      });
      return;
    }
    if (units.length === 0) return;
    if (shift) {
      queueingRef.current = true;
      void sendCmd({ type: "waypoint", unitIds: units, x: ent.x, z: ent.z });
      setCmdMsg("Waypoint");
      return;
    }
    if (queueingRef.current) {
      queueingRef.current = false;
      void sendCmd({ type: "waypoint", unitIds: units, x: ent.x, z: ent.z });
      setCmdMsg("Path set");
      return;
    }
    if (RESOURCES.has(ent.kind) || isFoodAnimal(ent.kind)) {
      const gatherers = units.filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        if (!u) return false;
        if (ent.kind === "fish") return u.kind === "fishing_boat";
        if (ent.kind === "oil_seep" || ent.kind === "gas_seep")
          return u.kind === "worker" || u.kind === "geologist";
        return u.kind === "worker";
      });
      if (gatherers.length) {
        void sendCmd({ type: "gather", unitIds: gatherers, resourceId: id });
        setCmdMsg("Gathering");
      }
      const rest = units.filter((uid) => !gatherers.includes(uid));
      if (rest.length) {
        if (isPredator(ent.kind)) {
          const warriors = rest.filter((uid) => {
            const u = room?.game?.entities.find((en) => en.id === uid);
            return !!u && WARRIOR_KINDS.has(u.kind);
          });
          if ((room.game?.attacksAt || 0) > Date.now()) {
            setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
          } else if (warriors.length) {
            void sendCmd({ type: "attack", unitIds: warriors, targetId: id });
            setCmdMsg("Attacking");
          } else setCmdMsg("Only warriors attack.");
        } else {
          void sendCmd({ type: "move", unitIds: rest, x: ent.x, z: ent.z });
          setCmdMsg("Moving");
        }
      }
      return;
    }
    if (ent.kind === "chrono") {
      void sendCmd({ type: "capture", unitIds: units, relicId: id });
      return;
    }
    // Board Cannon Ferry with selected land units
    const healers = units.filter((uid) => {
      const u = room?.game?.entities.find((en) => en.id === uid);
      return u?.kind === "healer";
    });
    if (
      healers.length &&
      ent.team &&
      ALL_UNITS.has(ent.kind) &&
      (ent.team === room.your_team || !isFoe(ent))
    ) {
      void sendCmd({ type: "heal", unitIds: healers, targetId: id });
      setCmdMsg("Healing");
      const rest = units.filter((uid) => !healers.includes(uid));
      if (rest.length && isFoe(ent)) {
        const warriors = rest.filter((uid) => {
          const u = room?.game?.entities.find((en) => en.id === uid);
          return !!u && WARRIOR_KINDS.has(u.kind);
        });
        if ((room.game?.attacksAt || 0) > Date.now()) {
          setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
        } else if (warriors.length) {
          void sendCmd({ type: "attack", unitIds: warriors, targetId: id });
        }
      }
      return;
    }
    if (ent.kind === "transport" && ent.team === room.your_team) {
      const riders = units.filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        return u && LAND_UNITS.has(u.kind);
      });
      if (riders.length) {
        void sendCmd({ type: "board", unitIds: riders, shipId: id });
        return;
      }
    }
    if (
      ent.team === room.your_team &&
      (BUILDINGS.has(ent.kind) || ent.kind === "town_hall") &&
      ent.hp < ent.maxHp
    ) {
      const fixers = units.filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        return u?.kind === "worker";
      });
      if (fixers.length) {
        void sendCmd({ type: "repair", unitIds: fixers, targetId: id });
        setCmdMsg("Repairing");
        return;
      }
    }
    if (isPredator(ent.kind) || isFoe(ent)) {
      const warriors = units.filter((uid) => {
        const u = room?.game?.entities.find((en) => en.id === uid);
        return !!u && WARRIOR_KINDS.has(u.kind);
      });
      const peace = (room.game?.attacksAt || 0) > Date.now();
      if (!warriors.length) {
        const priests = units.filter((uid) => {
          const u = room?.game?.entities.find((en) => en.id === uid);
          return u?.kind === "healer";
        });
        if (priests.length && ent.kind !== "town_hall") {
          if ((room.game?.attacksAt || 0) > Date.now()) {
            setCmdMsg("Peace holds for 25 minutes. No conversions yet.");
            return;
          }
          void sendCmd({ type: "convert", unitIds: priests, targetId: id });
          setCmdMsg("Converting");
          return;
        }
        setCmdMsg(
          ent.kind === "town_hall"
            ? "Villagers do not attack towns. Only warriors do, after the peace."
            : "Only warriors attack. The explorer only scouts."
        );
        return;
      }
      if (peace) {
        setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
        return;
      }
      void sendCmd({ type: "attack", unitIds: warriors, targetId: id });
      setCmdMsg("Attacking");
      return;
    }
    void sendCmd({ type: "move", unitIds: units, x: ent.x, z: ent.z });
    setCmdMsg("Moving");
  }

  function isFoe(ent: { team?: string | null; hp?: number }): boolean {
    if (!ent.team || !room?.your_team || ent.team === room.your_team) return false;
    if ((ent.hp ?? 1) <= 0) return false;
    const alliances = room.game?.alliances;
    if (
      alliances &&
      alliances[ent.team] != null &&
      alliances[ent.team] === alliances[room.your_team]
    ) {
      return false;
    }
    return true;
  }

  function onGroundRightClick(x: number, z: number, shift?: boolean) {
    if (!room?.your_team) return;
    if (buildMode) {
      const workers = selectedWorkers();
      void sendCmd({
        type: "build",
        unitId: workers[0] || "",
        unitIds: workers,
        building: buildMode,
        x,
        z,
      });
      return;
    }
    // Unload ferry at shore
    const ferry = selectedRef.current
      .map((id) => room.game?.entities.find((e) => e.id === id))
      .find((e) => e?.kind === "transport" && e.team === room.your_team);
    if (ferry && (ferry as { cargo?: number }).cargo) {
      void sendCmd({ type: "unload", shipId: ferry.id, x, z });
      return;
    }
    const unitIds = myUnits();
    if (attackGround && unitIds.length) {
      setAttackGround(false);
      void sendCmd({ type: "attack_ground", unitIds, x, z });
      setCmdMsg("Firing on that ground");
      return;
    }
    if (patrolArmed && unitIds.length) {
      setPatrolArmed(false);
      void sendCmd({ type: "patrol", unitIds, x, z });
      setCmdMsg("Patrolling");
      return;
    }
    if (unitIds.length && shift) {
      queueingRef.current = true;
      void sendCmd({ type: "waypoint", unitIds, x, z });
      setCmdMsg("Waypoint");
      return;
    }
    if (unitIds.length && queueingRef.current) {
      queueingRef.current = false;
      void sendCmd({ type: "waypoint", unitIds, x, z });
      setCmdMsg("Path set");
      return;
    }
    if (unitIds.length) {
      void sendCmd({ type: "move", unitIds, x, z });
      setCmdMsg("Moving");
      return;
    }
    const hall = selected
      .map((id) => room.game?.entities.find((e) => e.id === id))
      .find(
        (e) =>
          e &&
          e.team === room.your_team &&
          (e.kind === "town_hall" ||
            e.kind === "barracks" ||
            e.kind === "archery_range" ||
            e.kind === "stable" ||
            e.kind === "dock" ||
            e.kind === "siege_workshop")
      );
    if (hall) {
      void sendCmd({ type: "rally", buildingId: hall.id, x, z });
      setCmdMsg("Rally point set");
      return;
    }
    setCmdMsg("Select a unit, then right-click where they should go.");
  }

  function panTo(x: number, z: number) {
    lookN.current += 1;
    setLookAt({ x, z, n: lookN.current });
  }

  function selectIdleVillager() {
    const idle = (room?.game?.entities || []).filter(
      (e) =>
        e.kind === "worker" &&
        e.team === room?.your_team &&
        e.hp > 0 &&
        (!e.order || e.order === "idle")
    );
    if (!idle.length) {
      setCmdMsg("No idle villagers");
      return;
    }
    idleCursor.current = (idleCursor.current + 1) % idle.length;
    const v = idle[idleCursor.current];
    setSelected([v.id]);
    panTo(v.x, v.z);
    setCmdMsg("Idle villager");
  }

  function selectTownCenter() {
    const hall = room?.game?.entities.find(
      (e) => e.kind === "town_hall" && e.team === room?.your_team && e.hp > 0
    );
    if (!hall) return;
    setSelected([hall.id]);
    panTo(hall.x, hall.z);
  }

  function onFortBuildLine(
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    path?: Array<{ x: number; z: number }>,
    gates?: Array<{ x: number; z: number }>
  ) {
    if (!room?.your_team || !buildMode) return;
    if (
      buildMode !== "palisade" &&
      buildMode !== "wall" &&
      buildMode !== "fortified_wall" &&
      buildMode !== "gate"
    ) {
      return;
    }
    const workers = selectedWorkers();
    if (!workers.length && !room.game?.entities.some((e) => e.team === room.your_team && e.kind === "worker" && e.hp > 0)) {
      setCmdMsg("Need a villager to build");
      return;
    }
    void sendCmd({
      type: "build",
      unitId: workers[0] || "",
      unitIds: workers,
      building: buildMode,
      x: x0,
      z: z0,
      x1,
      z1,
      ...(path && path.length >= 2 ? { path } : {}),
      ...(gates && gates.length ? { gates } : {}),
    });
  }

  function onAttackMoveClick(x: number, z: number) {
    setAttackMove(false);
    if ((room?.game?.attacksAt || 0) > Date.now()) {
      setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
      return;
    }
    const unitIds = myUnits().filter((id) => {
      const u = room?.game?.entities.find((en) => en.id === id);
      return !!u && WARRIOR_KINDS.has(u.kind);
    });
    if (!unitIds.length) {
      setCmdMsg("Only warriors attack. Select them, tap A, then click the ground.");
      return;
    }
    void sendCmd({ type: "attack_move", unitIds, x, z });
  }

  function onGroundClick(_x: number, _z: number) {
    // Left-click ground: deselect (AoE-ish) unless building
    if (buildMode) {
      // Fort walls/gates: LMB drag-draw commits via onFortBuildLine
      if (
        buildMode === "palisade" ||
        buildMode === "wall" ||
        buildMode === "fortified_wall" ||
        buildMode === "gate"
      ) {
        return;
      }
      onGroundRightClick(_x, _z);
      return;
    }
    setSelected([]);
  }

  async function sendChat() {
    const text = chatDraft.trim();
    if (!text || !room || !token || chatBusy) return;
    setChatBusy(true);
    try {
      const res = await fetch(`/api/empire/rooms/${encodeURIComponent(room.code)}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, body: text }),
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.chat)) setChat(data.chat);
      setChatDraft("");
    } catch {
      /* ignore */
    } finally {
      setChatBusy(false);
    }
  }

  const eco = room?.your_team ? room.game?.eco?.[room.your_team] : null;
  const selEnt =
    selected.length === 1
      ? room?.game?.entities.find((e) => e.id === selected[0])
      : null;
  const nearbyGates =
    selEnt && room?.your_team
      ? (room.game?.entities || []).filter((e) => {
          if (e.kind !== "gate" || e.team !== room.your_team || e.hp <= 0) return false;
          const dx = e.x - selEnt.x;
          const dz = e.z - selEnt.z;
          return dx * dx + dz * dz <= 64;
        })
      : [];
  const selectedFort =
    !!selEnt &&
    !!room?.your_team &&
    selEnt.team === room.your_team &&
    FORT_KINDS.has(selEnt.kind);
  const workersSelected = selected.filter((id) => {
    const e = room?.game?.entities.find((en) => en.id === id);
    return e?.kind === "worker" && e.team === room?.your_team;
  });
  const unitsSelected = selected.filter((id) => {
    const e = room?.game?.entities.find((en) => en.id === id);
    return e && ALL_UNITS.has(e.kind) && e.team === room?.your_team;
  });
  const militarySelected = selected.filter((id) => {
    const e = room?.game?.entities.find((en) => en.id === id);
    return (
      e &&
      ALL_UNITS.has(e.kind) &&
      e.kind !== "worker" &&
      e.kind !== "explorer" &&
      e.kind !== "fishing_boat" &&
      e.kind !== "healer" &&
      e.team === room?.your_team
    );
  });
  const healersSelected = selected.filter((id) => {
    const e = room?.game?.entities.find((en) => en.id === id);
    return e?.kind === "healer" && e.team === room?.your_team && e.hp > 0;
  });
  const myMeta = room?.your_team ? room.game?.meta?.[room.your_team] : null;
  const myAge: Age = myMeta?.age || "dark";
  const chrono = room?.game?.chronoCharge || null;
  const matchActive =
    room?.status === "active" && !!room.game?.started && !room.game?.winner;
  const needsPracticeCta = !!room && !matchActive;

  const winnerLabel =
    room?.status === "finished" || room?.game?.winner
      ? room?.game?.winner === "draw" || room?.result === "draw"
        ? "Draw"
        : `${(room?.seats || []).find((s) => s.team === (room?.game?.winner || room?.result))?.name || (room?.game?.winner || room?.result)} wins`
      : null;

  const opponentName =
    (room?.seats || [])
      .filter((s) => s.team !== room?.your_team)
      .map((s) => (s.is_computer ? `${s.name}` : s.name))
      .slice(0, 4)
      .join(", ") ||
    room?.teal_name ||
    (room?.practice || room?.game?.practice ? "Passive" : "…");
  const myTeamLabel =
    (room?.seats || []).find((s) => s.team === room?.your_team)?.label ||
    (room?.your_team === "amber"
      ? "Amber"
      : room?.your_team === "teal"
        ? "Teal"
        : room?.your_team
          ? String(room.your_team)
          : "—");

  const villagerJobs: Record<string, number> = { wood: 0, food: 0, gold: 0, stone: 0, idle: 0, build: 0 };
  for (const e of room?.game?.entities || []) {
    if (e.kind !== "worker" || e.team !== room?.your_team || e.hp <= 0) continue;
    const job = e.job || (e.order === "idle" || !e.order ? "idle" : e.carry?.type || "idle");
    villagerJobs[job] = (villagerJobs[job] || 0) + 1;
  }

  const portraitLabel = selEnt
    ? selEnt.kind === "worker"
      ? "VILLAGER"
      : selEnt.kind === "explorer"
        ? "EXPLORER"
        : selEnt.kind.replaceAll("_", " ")
    : unitsSelected.length > 1
      ? `${unitsSelected.length} units`
      : "Nothing selected";

  return (
    <div className="empire-shafi-root">
      {!room ? (
        <div className="empire-lobby">
          <Link href="/games/" className="text-[14px] font-bold text-[var(--em-accent,#7dffb0)] hover:underline">
            ← Games
          </Link>
          <h1>Empire Skirmish</h1>
          <p>
            Real-time strategy · 100 maps · 2v2 practice · walls · Cannon Ferry · Chrono Vaults
            
          </p>

          <div className="empire-panel">
            <label className="block text-[13px] text-[var(--em-muted,#8aa090)]">
              Your name
              <input
                className="empire-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={32}
                placeholder="How others see you"
              />
            </label>
            {lobbyMsg && (
              <p className="mt-3 text-[13px] font-semibold text-[var(--em-warn,#ff7a5c)]">{lobbyMsg}</p>
            )}
          </div>

          <div className="empire-panel">
            <h2>Choose a map</h2>
            <MapPicker selected={mapId} onSelect={chooseMap} />
            <p className="mt-3 text-[13px] font-bold">Who do you want to play against?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(["easy", "normal", "hard"] as Difficulty[]).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`empire-btn${difficulty === d ? " empire-btn-primary" : ""}`}
                  onClick={() => setDifficulty(d)}
                >
                  {d === "easy" ? "Easy" : d === "normal" ? "Normal" : "Hard"}
                </button>
              ))}
            </div>
            <p className="mt-3 text-[13px] font-bold">Your people</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {CIVS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  title={c.note}
                  className={`empire-btn${civ === c.id ? " empire-btn-primary" : ""}`}
                  onClick={() => setCiv(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <p className="mt-3 text-[13px] font-bold">What to play</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {MISSIONS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  title={m.note}
                  className={`empire-btn${mission === m.id ? " empire-btn-primary" : ""}`}
                  onClick={() => {
                    setMission(m.id);
                    setDifficulty(m.difficulty);
                  }}
                >
                  {m.name}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className="empire-btn empire-btn-primary" onClick={() => create(true)}>
                Play this map
              </button>
              <button type="button" className="empire-btn" onClick={() => create(false)}>
                {selectedStake() > 0
                  ? `Host 8p · stake ${fmtTokens(selectedStake())}`
                  : "Host skirmish (up to 8)"}
              </button>
            </div>
          </div>

          {joinCode ? (
            <div className="empire-panel" style={{ borderColor: "rgba(125,255,176,0.45)" }}>
              <h2>Join room {joinCode}</h2>
              <button
                type="button"
                className="empire-btn empire-btn-primary mt-2 w-full"
                onClick={() => join(joinCode)}
              >
                Join {joinCode}
              </button>
            </div>
          ) : null}

          <div className="empire-panel">
            <h2>Open rooms</h2>
            {openRooms.length === 0 ? (
              <p className="mt-3 text-center text-[14px] text-[var(--em-muted,#8aa090)]">
                No skirmishes open — start practice above.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {openRooms.map((r) => (
                  <li key={r.code} className="empire-lobby-list-item">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-[#f4fff8]">{r.host_name}</div>
                      <div className="text-[12px] text-[var(--em-muted,#8aa090)]">
                        {r.practice
                          ? "practice"
                          : r.seat_open
                            ? "1 waiting"
                            : "2 playing"}
                        {(r.stake_amount || 0) > 0
                          ? ` · ${fmtTokens(r.stake_amount)} stake`
                          : ""}
                      </div>
                    </div>
                    <span className="empire-room-code">{r.code}</span>
                    {r.seat_open || (r.open_computer_seats || 0) > 0 ? (
                      <button
                        type="button"
                        className="empire-btn empire-btn-primary"
                        onClick={() => join(r.code)}
                      >
                        Join
                      </button>
                    ) : (
                      <span className="text-[12px] text-[var(--em-muted,#8aa090)]">Full</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <input
                className="empire-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ROOM CODE"
                maxLength={8}
              />
              <button type="button" className="empire-btn" onClick={() => join(joinCode)}>
                Join
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="empire-play-shell flex w-full flex-col px-2 py-1.5 sm:px-3" ref={shellRef}>
          {toast ? <div className="empire-toast">{toast}</div> : null}

          {mapOpen ? (
            <div className="empire-map-overlay">
              <div className="empire-lobby">
                <h2>Choose a map</h2>
                <MapPicker selected={mapId} onSelect={chooseMap} />
                <p className="mt-3 text-[13px] font-bold">Who do you want to play against?</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(["easy", "normal", "hard"] as Difficulty[]).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`empire-btn${difficulty === d ? " empire-btn-primary" : ""}`}
                      onClick={() => setDifficulty(d)}
                    >
                      {d === "easy" ? "Easy" : d === "normal" ? "Normal" : "Hard"}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {CIVS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      title={c.note}
                      className={`empire-btn${civ === c.id ? " empire-btn-primary" : ""}`}
                      onClick={() => setCiv(c.id)}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {MISSIONS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      title={m.note}
                      className={`empire-btn${mission === m.id ? " empire-btn-primary" : ""}`}
                      onClick={() => {
                        setMission(m.id);
                        setDifficulty(m.difficulty);
                      }}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" className="empire-btn empire-btn-primary" onClick={() => void create(true)}>
                    Play this map
                  </button>
                  <button type="button" className="empire-btn" onClick={() => setMapOpen(false)}>
                    Back
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="empire-topbar">
            <Link href="/games/">← Games</Link>
            <span className="empire-room-code">{room.code}</span>
            {room.practice || room.game?.practice ? (
              <span className="empire-pill">Practice</span>
            ) : null}
            <span className="empire-meta">
              {myTeamLabel} vs {opponentName}
            </span>
            {room.game?.mapName ? (
              <span className="empire-pill empire-pill-muted">{room.game.mapName}</span>
            ) : null}
            {room.game?.attacksAt && room.game.attacksAt > Date.now() ? (
              <span className="empire-pill" title="No one can attack until this ends">
                Peace{" "}
                {Math.floor(Math.max(0, room.game.attacksAt - Date.now()) / 60000)}:
                {String(
                  Math.floor((Math.max(0, room.game.attacksAt - Date.now()) % 60000) / 1000)
                ).padStart(2, "0")}
              </span>
            ) : null}
            <button type="button" className="empire-btn" onClick={() => setMapOpen(true)}>
              Maps
            </button>
            <button type="button" className="empire-btn" onClick={() => setSettingsOpen((v) => !v)}>
              Menu
            </button>
            <button
              type="button"
              className="empire-btn"
              onClick={() => setGuideOpen((v) => !v)}
            >
              Cheatsheet
            </button>
            <button type="button" className="empire-btn" onClick={() => void toggleFullscreen()}>
              {isFullscreen ? "Window" : "Full"}
            </button>
            <button type="button" className="empire-btn empire-btn-danger ml-auto" onClick={leave}>
              Resign
            </button>
          </div>

          <div className="empire-score" aria-label="Resources">
            <div className="empire-score-age" title={AGE_LABELS[myAge]}>
              {myAge === "imperial" ? "IV" : myAge === "castle" ? "III" : myAge === "feudal" ? "II" : "I"}
            </div>
            {(
              [
                ["wood", "Wood", eco?.wood ?? 0],
                ["food", "Food", eco?.food ?? 0],
                ["gold", "Gold", eco?.gold ?? 0],
                ["stone", "Stone", eco?.stone ?? 0],
              ] as Array<[string, string, number]>
            ).map(([kind, label, amount]) => (
              <div className="empire-score-col" key={kind}>
                <span className="empire-score-ico" title={label}><HudIcon kind={kind} /></span>
                <b>{amount}</b>
                <span className="empire-score-job" title={`Villagers on ${label}`}>
                  <HudIcon kind={kind} />
                  {villagerJobs[kind] || 0}
                </span>
              </div>
            ))}
            <div className="empire-score-col empire-score-pop">
              <span className="empire-score-ico" title="Population"><HudIcon kind="house" /></span>
              <b>{myMeta?.pop ?? 0}/{myMeta?.popCap ?? 200}</b>
              <span className="empire-score-job" title="Idle villagers">
                <HudIcon kind="idle" />
                {villagerJobs.idle || 0}
              </span>
            </div>
          </div>

          {guideOpen ? (
            <div className="empire-panel mb-2 text-[13px] leading-relaxed">
              <strong className="block mb-1">Empire field guide</strong>
              <p className="m-0 text-[var(--em-muted,#8aa090)]">
                Box-select or Ctrl+A villagers (2–12+) → pick People count → toggle Wood/Food/Gold/Stone → Divide Work (.) to split
                jobs evenly; each villager auto-takes its own closest remembered source. Or click
                tree / berry / gold / stone / farm / livestock to gather. Predators hunt villagers
                in the wild — Town Centers keep the plaza wood/food line safe. Click oil/gas seeps with a villager or University-trained Geologist to drill wells. Beaches, docks, and lighthouses line the shores.
                Market: buy/sell 100 food/wood/stone. Ages Dark→Imperial. WASD pan · LMB select ·
                RMB order.
              </p>
            </div>
          ) : null}

          {settingsOpen ? (
            <div className="empire-panel mb-2 text-[13px]">
              <label className="block">
                Camera pan ({panSpeed.toFixed(2)})
                <input
                  type="range"
                  min={0.4}
                  max={2}
                  step={0.05}
                  value={panSpeed}
                  onChange={(e) => setPanSpeed(Number(e.target.value))}
                  className="mt-1 w-full"
                />
              </label>
              <label className="mt-2 block">
                Graphics
                <select
                  className="empire-input"
                  value={quality}
                  onChange={(e) =>
                    setQuality(e.target.value as GraphicsQuality)
                  }
                >
                  <option value="low">Low (mobile)</option>
                  <option value="medium">Medium</option>
                  <option value="high">High (desktop GPU)</option>
                </select>
              </label>
              <label className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={preferHighPerfGpu}
                  onChange={(e) => setPreferHighPerfGpu(e.target.checked)}
                />
                Use high-performance GPU (local WebGL)
              </label>
              <label className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sfxMuted}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setSfxMuted(v);
                    setEmpireMuted(v);
                    if (!v) playUiClick();
                  }}
                />
                Mute game sounds
              </label>
              <p className="mt-2 text-[11px] text-[var(--em-muted,#8aa090)]">
                Renders on your machine&apos;s GPU via WebGL. WASD / MMB pan · LMB select · RMB
                order.
              </p>
              <button type="button" className="empire-btn mt-2" onClick={() => setSettingsOpen(false)}>
                Close
              </button>
            </div>
          ) : null}

          {room.your_role === "can_join" && (room.open_computer_seats || 0) > 0 ? (
            <div className="empire-waiting-banner">
              <p>
                Live skirmish — {room.open_computer_seats} computer{" "}
                {(room.open_computer_seats || 0) === 1 ? "city" : "cities"} open. Join mid-game.
              </p>
              <button
                type="button"
                className="empire-btn empire-btn-amber"
                onClick={() => void join(room.code)}
              >
                Claim a computer city
              </button>
            </div>
          ) : null}

          {room.status === "waiting" ? (
            <div className="empire-waiting-banner">
              <p>
                Waiting for opponent — share <strong>{room.code}</strong>, or practice now.
              </p>
              <button
                type="button"
                className="empire-btn empire-btn-primary"
                onClick={() => setMapOpen(true)}
              >
                Choose a map
              </button>
            </div>
          ) : null}

          {chrono && matchActive ? (
            <div className="empire-chrono-banner">
              <span>
                {chrono.name} charged — send opponent back {minsLabel(chrono.rewindSec)}
              </span>
              <button
                type="button"
                className="empire-btn empire-btn-primary"
                onClick={() => void sendCmd({ type: "rewind" })}
              >
                Chronoshift enemy
              </button>
            </div>
          ) : null}

          <div className="relative">
            <div className="empire-objective">HOLD THE HIGH GROUND</div>
            {attackMove ? <div className="empire-attack-move">Attack-move — click the ground</div> : null}
            {patrolArmed ? <div className="empire-attack-move">Patrol — right-click the far point</div> : null}
            {attackGround ? <div className="empire-attack-move">Attack ground — right-click the spot</div> : null}
            <EmpireWorld3D
              key={`${room.game?.mapId || "map"}-${room.game?.mapSeed || 0}`}
              entities={room.game?.entities || []}
              selectedIds={selected}
              onSelect={onSelect}
              onBoxSelect={onBoxSelect}
              onGroundClick={onGroundClick}
              onGroundRightClick={onGroundRightClick}
              onEntityRightClick={onEntityRightClick}
              onFortBuildLine={onFortBuildLine}
              onFortCancel={() => setBuildMode(null)}
              attackMoveArmed={attackMove}
              onAttackMove={onAttackMoveClick}
              fortBuildMode={
                buildMode === "palisade" ||
                buildMode === "wall" ||
                buildMode === "fortified_wall" ||
                buildMode === "gate"
              }
              placeBuildMode={
                !!buildMode &&
                buildMode !== "palisade" &&
                buildMode !== "wall" &&
                buildMode !== "fortified_wall" &&
                buildMode !== "gate"
              }
              fortBuildGate={buildMode === "gate"}
              panSpeed={panSpeed}
              fog={room.game?.fog ?? null}
              water={room.game?.water ?? null}
              beaches={room.game?.beaches ?? []}
              myTeam={room.your_team}
              interactive={!!matchActive}
              quality={quality}
              preferHighPerfGpu={preferHighPerfGpu}
              mapSeed={room.game?.mapSeed ?? 11}
              mapBiome={room.game?.mapBiome ?? null}
              lookAt={lookAt}
            >
          <div className="empire-chat-dock rounded border border-[#5c4030] bg-[#1a140e] p-2">
            <div
              ref={chatListRef}
              className="max-h-[80px] space-y-1 overflow-y-auto px-2 py-1 text-[13px]"
            >
              {chat.length === 0 ? (
                <p className="text-center text-[12px] text-[#a89070]">No messages</p>
              ) : (
                chat.map((m) => (
                  <div key={m.id}>
                    <span className="font-bold text-[#f0d78c]">{m.name}</span>
                    <span className="text-[#a89070]"> · </span>
                    <span>{m.body}</span>
                  </div>
                ))
              )}
            </div>
            <form
              className="mt-1 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void sendChat();
              }}
            >
              <input
                className="empire-input"
                value={chatDraft}
                onChange={(e) => setChatDraft(e.target.value)}
                maxLength={280}
                placeholder="Dispatch…"
              />
              <button
                type="submit"
                className="empire-btn"
                disabled={chatBusy || !chatDraft.trim()}
              >
                Send
              </button>
            </form>
          </div>
            </EmpireWorld3D>
            {winnerLabel ? (
              <div className="empire-gameover-modal">
                <div className="empire-gameover-card">
                  <h3>{winnerLabel}</h3>
                  <p>Match over. Start a fresh practice to control workers again.</p>
                  <button
                    type="button"
                    className="empire-btn empire-btn-primary"
                    onClick={() => setMapOpen(true)}
                  >
                    Choose a map
                  </button>
                </div>
              </div>
            ) : needsPracticeCta && room.status !== "waiting" ? (
              <div className="empire-practice-cta">
                <p>Ready when you are</p>
                <span className="empire-cta-sub">
                  Match isn&apos;t active — start practice to select villagers and play.
                </span>
                <button
                  type="button"
                  className="empire-btn empire-btn-primary"
                  onClick={() => setMapOpen(true)}
                >
                  Choose a map
                </button>
              </div>
            ) : null}
          </div>

          <div className="empire-cmd-bar">
            <div className="empire-aoe-left">
            <div className="empire-portrait">
              <strong>{portraitLabel}</strong>
              {selEnt?.kind === "worker" ? <div className="empire-role">Worker</div> : null}
              {selEnt?.kind === "explorer" ? (
                <div className="empire-role">Right-click the ground. He goes there and stays.</div>
              ) : null}
              {selEnt ? (
                <div>
                  <span className="empire-strength">
                    {selEnt.hp}/{selEnt.maxHp}
                    <span className="empire-strength-track" aria-hidden>
                      <span
                        style={{
                          width: `${Math.max(0, Math.min(100, (selEnt.hp / Math.max(1, selEnt.maxHp)) * 100))}%`,
                        }}
                      />
                    </span>
                  </span>
                  {selEnt.order ? ` · ${selEnt.order}` : ""}
                  {selEnt.carry?.amount
                    ? ` · carry ${selEnt.carry.amount} ${selEnt.carry.type}`
                    : ""}
                  {selEnt.train
                    ? ` · training ${selEnt.train.unit} (${selEnt.train.left})`
                    : ""}
                  {selEnt.research
                    ? ` · research ${selEnt.research.tech} (${selEnt.research.left})`
                    : ""}
                  {selEnt.kind === "transport" && selEnt.cargo
                    ? ` · cargo ${selEnt.cargo}`
                    : ""}
                  {selEnt.kind === "gate" && selEnt.gateState
                    ? ` · ${selEnt.gateState}`
                    : ""}
                </div>
              ) : (
                <div>
                  {matchActive
                    ? `Select · RMB order${room.game?.mapName ? ` · Map: ${room.game.mapName}` : ""}`
                    : "Play Practice to take control"}
                </div>
              )}
              {cmdMsg ? (
                <div className="mt-1 font-semibold text-[var(--em-amber,#ffb84a)]">{cmdMsg}</div>
              ) : null}
            </div>
            </div>
            <div className="empire-aoe-card">
              <div className="empire-aoe-tabs">
                <button type="button" className={buildTab === "military" ? "on" : ""} onClick={() => setBuildTab("military")} title="Military">
                  <HudIcon kind="barracks" />
                </button>
                <button type="button" className={buildTab === "economy" ? "on" : ""} onClick={() => setBuildTab("economy")} title="Economy">
                  <HudIcon kind="house" />
                </button>
                <button type="button" className={`walls${buildTab === "walls" ? " on" : ""}`} onClick={() => setBuildTab("walls")} title="Walls">
                  <HudIcon kind="wall" />
                </button>
              </div>
              <div className="empire-row-labels" aria-hidden>
                <span>I Q</span>
                <span>II W</span>
                <span>III E</span>
                <span>IV R</span>
              </div>
            <div
              className="empire-cmd-grid"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("button.empire-cmd-cell")) {
                  playUiClick();
                }
              }}
            >
              {matchActive ? (
                <>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    title="Send selected villagers to the nearest tree"
                    onClick={() => {
                      if (!workersSelected.length) {
                        setCmdMsg("Select a villager");
                        return;
                      }
                      const wood = nearestGatherNode(["wood"], workersSelected);
                      if (wood)
                        void sendCmd({
                          type: "gather",
                          unitIds: workersSelected,
                          resourceId: wood.id,
                        });
                      else setCmdMsg("No wood in vision");
                    }}
                  >
                    Gather Lumber
                  </button>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    title="Send selected villagers to the nearest food"
                    onClick={() => {
                      if (!workersSelected.length) {
                        setCmdMsg("Select a villager");
                        return;
                      }
                      const food = nearestGatherNode(
                        ["farm", "food", "sheep", "chicken", "deer", "cow", "goat", "duck"],
                        workersSelected
                      );
                      if (food)
                        void sendCmd({
                          type: "gather",
                          unitIds: workersSelected,
                          resourceId: food.id,
                        });
                      else setCmdMsg("No food in vision");
                    }}
                  >
                    Gather Food
                  </button>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    title="Send selected villagers to the nearest stone"
                    onClick={() => {
                      if (!workersSelected.length) {
                        setCmdMsg("Select a villager");
                        return;
                      }
                      const s = nearestGatherNode(["stone"], workersSelected);
                      if (s)
                        void sendCmd({
                          type: "gather",
                          unitIds: workersSelected,
                          resourceId: s.id,
                        });
                      else setCmdMsg("No stone in vision");
                    }}
                  >
                    Gather Stone
                  </button>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    title="Send selected villagers to the nearest gold"
                    onClick={() => {
                      if (!workersSelected.length) {
                        setCmdMsg("Select a villager");
                        return;
                      }
                      const g = nearestGatherNode(["gold"], workersSelected);
                      if (g)
                        void sendCmd({
                          type: "gather",
                          unitIds: workersSelected,
                          resourceId: g.id,
                        });
                      else setCmdMsg("No gold in vision");
                    }}
                  >
                    Gather Gold
                  </button>
                </>
              ) : null}
              {nearbyGates.length > 1 || (nearbyGates.length > 0 && !selectedFort) ? (
                <button
                  id="empire-raze-fort"
                  type="button"
                  className="empire-cmd-cell empire-delete"
                  disabled={!matchActive}
                  title="Remove every gate piled on this spot. You get half the stone back."
                  onClick={() => selEnt && void razeFort(selEnt.id, true)}
                >
                  Delete {nearbyGates.length} gate{nearbyGates.length === 1 ? "" : "s"} here
                </button>
              ) : null}
              {selectedFort ? (
                <button
                  id={nearbyGates.length > 1 ? undefined : "empire-raze-fort"}
                  type="button"
                  className="empire-cmd-cell empire-delete"
                  disabled={!matchActive}
                  title="Delete this one segment. Delete key does the same."
                  onClick={() => selEnt && void razeFort(selEnt.id, false)}
                >
                  {selEnt?.kind === "gate" ? "Delete this gate" : "Delete this wall"}
                </button>
              ) : null}
              {selEnt && TRAIN_BY_BUILDING[selEnt.kind] && room.your_team
                ? TRAIN_BY_BUILDING[selEnt.kind].map((unit) => {
                    const locked = AGE_RANK_CLIENT(UNIT_AGE[unit]) > AGE_RANK_CLIENT(myAge);
                    return (
                      <button
                        key={unit}
                        type="button"
                        className="empire-cmd-cell"
                        disabled={!matchActive || !!selEnt.constructing || locked}
                        title={locked ? `Need ${AGE_LABELS[UNIT_AGE[unit]]}` : formatCost(COST[unit])}
                        onClick={() =>
                          sendCmd({ type: "train", buildingId: selEnt.id, unit })
                        }
                      >
                        {UNIT_LABELS[unit]} {formatCost(COST[unit])}
                        {locked ? " (locked)" : ""}
                      </button>
                    );
                  })
                : null}
              {selEnt && RESEARCH_BY_BUILDING[selEnt.kind] && room.your_team
                ? RESEARCH_BY_BUILDING[selEnt.kind].map((tech) => (
                    <button
                      key={tech}
                      type="button"
                      className="empire-cmd-cell"
                      disabled={!matchActive || !!selEnt.constructing || !!selEnt.train}
                      title={formatCost(COST[tech])}
                      onClick={() =>
                        sendCmd({ type: "research", buildingId: selEnt.id, tech })
                      }
                    >
                      {TECH_LABELS[tech]} {formatCost(COST[tech])}
                    </button>
                  ))
                : null}
              {selEnt?.kind === "market" && room.your_team
                ? (() => {
                    const prices = room.game?.marketPrices || defaultMarketPrices();
                    const busy = !matchActive || !!selEnt.constructing;
                    return (
                      <>
                        {MARKET_GOODS.map((good: MarketGood) => (
                          <button
                            key={`buy-${good}`}
                            type="button"
                            className="empire-cmd-cell"
                            disabled={busy}
                            title={`Buy ${MARKET_LOT} ${MARKET_GOOD_LABELS[good]}`}
                            onClick={() =>
                              sendCmd({ type: "market_buy", good, amount: MARKET_LOT })
                            }
                          >
                            Buy {MARKET_GOOD_LABELS[good]}
                            <br />
                            {MARKET_LOT} / {prices[good].buy}G
                          </button>
                        ))}
                        {MARKET_GOODS.map((good: MarketGood) => (
                          <button
                            key={`sell-${good}`}
                            type="button"
                            className="empire-cmd-cell"
                            disabled={busy}
                            title={`Sell ${MARKET_LOT} ${MARKET_GOOD_LABELS[good]} for gold`}
                            onClick={() => {
                              setCmdMsg(`Sold ${MARKET_LOT} ${good} for ${prices[good].sell} gold`);
                              void sendCmd({ type: "market_sell", good, amount: MARKET_LOT });
                            }}
                          >
                            Sell {MARKET_GOOD_LABELS[good]}
                            <br />
                            {MARKET_LOT} for {prices[good].sell} gold
                          </button>
                        ))}
                        {(
                          [
                            ["food", "wood"],
                            ["food", "stone"],
                            ["wood", "food"],
                            ["wood", "stone"],
                            ["stone", "food"],
                            ["stone", "wood"],
                            ["gold", "food"],
                            ["gold", "wood"],
                            ["gold", "stone"],
                          ] as Array<["food" | "wood" | "gold" | "stone", "food" | "wood" | "gold" | "stone"]>
                        ).map(([give, take]) => {
                          const goldIn = give === "gold" ? 100 : prices[give].sell;
                          const recv =
                            take === "gold" ? goldIn : Math.floor((goldIn * 100) / prices[take].buy);
                          return (
                            <button
                              key={`${give}-${take}`}
                              type="button"
                              className="empire-cmd-cell"
                              disabled={busy || recv < 1}
                              title="Gold is the coin. Give 100, receive the other at the current rate."
                              onClick={() => {
                                setCmdMsg(`Trade 100 ${give} for ${recv} ${take}`);
                                void sendCmd({ type: "market_swap", give, take });
                              }}
                            >
                              100 {give} → {recv} {take}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          className="empire-cmd-cell"
                          disabled={busy}
                          title="Send a cart to an allied market or town hall"
                          onClick={() => {
                            setCmdMsg("Trade cart heading to your ally");
                            void sendCmd({ type: "trade_route" });
                          }}
                        >
                          Trade with ally
                        </button>
                      </>
                    );
                  })()
                : null}
              {selEnt?.research ? (
                <span className="empire-cmd-cell" style={{ opacity: 0.7 }}>
                  Research {selEnt.research.tech} ({selEnt.research.left})
                </span>
              ) : null}
              {selEnt?.kind === "gate" && room.your_team && selEnt.team === room.your_team ? (
                <>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    disabled={!matchActive}
                    title="Lock / unlock gate"
                    onClick={() =>
                      sendCmd({
                        type: "toggle_gate",
                        gateId: selEnt.id,
                        mode: selEnt.gateState === "locked" ? "closed" : "locked",
                      })
                    }
                  >
                    {selEnt.gateState === "locked" ? "Unlock Gate" : "Lock Gate"}
                  </button>
                </>
              ) : null}
              {matchActive
                ? (buildTab === "walls"
                    ? (["palisade", "wall", "fortified_wall", "gate"] as BuildKind[])
                    : buildTab === "military"
                      ? (["barracks", "archery_range", "stable", "siege_workshop", "watch_tower", "keep", "blacksmith", "university"] as BuildKind[])
                      : (["house", "dock", "farm", "granary", "storage_pit", "mill", "lumber_camp", "mining_camp", "market", "church", "wonder"] as BuildKind[])
                  ).map((b, i) => {
                    const locked = AGE_RANK_CLIENT(BUILD_AGE[b]) > AGE_RANK_CLIENT(myAge);
                    const hk = ["Q", "W", "E", "R", "T", "F", "G", "Z"][i] || "";
                    return (
                      <button
                        key={b}
                        type="button"
                        className={`empire-cmd-cell${buildMode === b ? " active" : ""}`}
                        disabled={!matchActive || locked}
                        title={locked ? `Need ${AGE_LABELS[BUILD_AGE[b]]}` : `${BUILD_LABELS[b]} ${formatCost(COST[b])}`}
                        onClick={() => setBuildMode((m) => (m === b ? null : b))}
                      >
                        <i className="hk">{hk}</i>
                        <HudIcon kind={b} />
                        <span>{BUILD_LABELS[b]}</span>
                      </button>
                    );
                  })
                : null}
              {matchActive ? (
                <>
                  {DIVIDE_TASKS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`empire-cmd-cell${divideTasks.includes(t.id) ? " active" : ""}`}
                      title={`Toggle ${t.label} for Divide Work`}
                      onClick={() => toggleDivideTask(t.id)}
                    >
                      {divideTasks.includes(t.id) ? "✓ " : ""}
                      {t.label}
                    </button>
                  ))}
                  <div className="empire-cmd-cell" style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", gridColumn: "1 / -1" }}>
                    <span style={{ opacity: 0.75, fontSize: 11, marginRight: 4 }}>People</span>
                    <button
                      type="button"
                      className={`empire-cmd-cell${dividePeople === "all" ? " active" : ""}`}
                      style={{ padding: "2px 8px", minWidth: 0 }}
                      title="Use all selected villagers"
                      onClick={() => setDividePeople("all")}
                    >
                      All{workersSelected.length ? ` (${workersSelected.length})` : ""}
                    </button>
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`empire-cmd-cell${dividePeople === n ? " active" : ""}`}
                        style={{ padding: "2px 6px", minWidth: 0 }}
                        disabled={workersSelected.length > 0 && workersSelected.length < n}
                        title={`Use ${n} of the selected villagers`}
                        onClick={() => setDividePeople(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <button
                    id="empire-divide-work"
                    type="button"
                    className="empire-cmd-cell active"
                    disabled={!divideTasks.length}
                    title="Split selected villagers evenly across checked tasks (hotkey .). Box-select 2–12+ then Divide Work."
                    onClick={() => runDivideWork()}
                  >
                    Divide Work
                    {(() => {
                      const n =
                        dividePeople === "all"
                          ? workersSelected.length || ""
                          : Math.min(workersSelected.length || dividePeople, dividePeople);
                      return n ? ` · ${n}` : divideTasks.length ? ` (${divideTasks.length})` : "";
                    })()}
                  </button>
                </>
              ) : null}
              {healersSelected.length > 0 ? (
                <button
                  type="button"
                  className="empire-cmd-cell"
                  disabled={!matchActive}
                  onClick={() => {
                    const mine = room.game?.entities.find((u) => u.id === healersSelected[0]);
                    const hurt = (room.game?.entities || [])
                      .filter(
                        (e) =>
                          e.hp > 0 &&
                          e.maxHp > e.hp &&
                          e.team &&
                          !isFoe(e) &&
                          ALL_UNITS.has(e.kind)
                      )
                      .sort(
                        (a, b) =>
                          Math.hypot(a.x - (mine?.x ?? 0), a.z - (mine?.z ?? 0)) -
                          Math.hypot(b.x - (mine?.x ?? 0), b.z - (mine?.z ?? 0))
                      )[0];
                    if (!hurt) {
                      setCmdMsg("Nobody is hurt");
                      return;
                    }
                    void sendCmd({ type: "heal", unitIds: healersSelected, targetId: hurt.id });
                    setCmdMsg("Healing");
                  }}
                >
                  Heal
                </button>
              ) : null}
              {unitsSelected.length > 0 ? (
                <button
                  type="button"
                  className="empire-cmd-cell"
                  disabled={!matchActive}
                  onClick={() => {
                    void sendCmd({ type: "stop", unitIds: unitsSelected });
                    setCmdMsg("Stopped");
                  }}
                >
                  Stop
                </button>
              ) : null}
              {militarySelected.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    disabled={!matchActive}
                    onClick={() => {
                      void sendCmd({ type: "stance", unitIds: militarySelected, stance: "stand" });
                      setCmdMsg("Stand ground");
                    }}
                  >
                    Stand Ground
                  </button>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    disabled={!matchActive}
                    onClick={() => {
                      void sendCmd({ type: "stance", unitIds: militarySelected, stance: "aggressive" });
                      setCmdMsg("Aggressive");
                    }}
                  >
                    Aggressive
                  </button>
                  <button
                    type="button"
                    className={`empire-cmd-cell${patrolArmed ? " active" : ""}`}
                    disabled={!matchActive}
                    onClick={() => {
                      setPatrolArmed(true);
                      setAttackMove(false);
                      setCmdMsg("Patrol — right-click the far point");
                    }}
                  >
                    Patrol
                  </button>
                  {militarySelected.some((id) => {
                    const k = room.game?.entities.find((e) => e.id === id)?.kind;
                    return k === "mangonel" || k === "scorpion" || k === "trebuchet" || k === "ram";
                  }) ? (
                    <button
                      type="button"
                      className={`empire-cmd-cell${attackGround ? " active" : ""}`}
                      onClick={() => {
                        setAttackGround(true);
                        setPatrolArmed(false);
                        setCmdMsg("Attack ground — right-click the spot");
                      }}
                    >
                      Attack Ground
                    </button>
                  ) : null}
                </>
              ) : null}
              {workersSelected.length > 0 ? (
                <button
                  type="button"
                  className="empire-cmd-cell"
                  disabled={!matchActive}
                  onClick={() => {
                    const hurt = (room.game?.entities || [])
                      .filter(
                        (e) =>
                          e.team === room.your_team &&
                          e.hp > 0 &&
                          e.hp < e.maxHp &&
                          (BUILDINGS.has(e.kind) || e.kind === "town_hall")
                      )
                      .sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0];
                    if (!hurt) {
                      setCmdMsg("Nothing damaged to repair");
                      return;
                    }
                    void sendCmd({ type: "repair", unitIds: workersSelected, targetId: hurt.id });
                    setCmdMsg("Repairing");
                  }}
                >
                  Repair
                </button>
              ) : null}
              {militarySelected.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    disabled={!matchActive}
                    onClick={() => {
                      const foes = (room.game?.entities || []).filter((e) => isFoe(e));
                      const enemy =
                        foes.find((e) => e.kind === "town_hall") ||
                        foes.slice().sort((a, b) => {
                          const ax = militarySelected.length
                            ? room.game?.entities.find((u) => u.id === militarySelected[0])
                            : null;
                          const ox = ax?.x ?? 0;
                          const oz = ax?.z ?? 0;
                          return (
                            Math.hypot(a.x - ox, a.z - oz) - Math.hypot(b.x - ox, b.z - oz)
                          );
                        })[0];
                      if ((room.game?.attacksAt || 0) > Date.now()) {
                        setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
                        return;
                      }
                      if (enemy) {
                        void sendCmd({
                          type: "attack",
                          unitIds: militarySelected,
                          targetId: enemy.id,
                        });
                        setCmdMsg("Attacking");
                      } else setCmdMsg("No enemy in vision");
                    }}
                  >
                    Attack
                  </button>
                  <button
                    type="button"
                    className="empire-cmd-cell"
                    disabled={!matchActive}
                    onClick={() => {
                      const enemyTc = room.game?.entities.find(
                        (e) =>
                          e.kind === "town_hall" &&
                          e.team &&
                          e.team !== room.your_team
                      );
                      if ((room.game?.attacksAt || 0) > Date.now()) {
                        setCmdMsg("Peace holds for 25 minutes. Warriors cannot attack yet.");
                        return;
                      }
                      void sendCmd({
                        type: "attack_move",
                        unitIds: militarySelected,
                        x: enemyTc?.x ?? 0,
                        z: enemyTc?.z ?? 0,
                      });
                    }}
                  >
                    Attack-Move
                  </button>
                </>
              ) : null}
              {chrono ? (
                <button
                  type="button"
                  className="empire-cmd-cell active"
                  disabled={!matchActive}
                  onClick={() => void sendCmd({ type: "rewind" })}
                >
                  Chronoshift {minsLabel(chrono.rewindSec)}
                </button>
              ) : null}
              <button
                id="empire-select-villagers"
                type="button"
                className="empire-cmd-cell active"
                title="Select all villagers (Ctrl+A)"
                onClick={() => selectAllVillagers()}
              >
                Select Villagers
              </button>
              <button
                id="empire-idle-villager"
                type="button"
                className="empire-cmd-cell"
                title="Next idle villager (comma)"
                onClick={() => selectIdleVillager()}
              >
                Idle Villager
              </button>
              {unitsSelected.length > 0 ? (
                <button
                  type="button"
                  className="empire-cmd-cell"
                  title="Alt-right-click the town hall does this too"
                  onClick={() => {
                    const hall = room.game?.entities.find(
                      (e) => e.kind === "town_hall" && e.team === room.your_team && e.hp > 0
                    );
                    if (!hall) return;
                    void sendCmd({ type: "garrison", unitIds: unitsSelected, buildingId: hall.id });
                    setCmdMsg("Inside the town hall");
                  }}
                >
                  Garrison
                </button>
              ) : null}
              {selEnt && (selEnt.kind === "town_hall" || selEnt.kind === "watch_tower" || selEnt.kind === "keep") ? (
                <button
                  type="button"
                  className="empire-cmd-cell"
                  onClick={() => {
                    void sendCmd({ type: "ungarrison", buildingId: selEnt.id });
                    setCmdMsg("They came out");
                  }}
                >
                  Ungarrison
                </button>
              ) : null}
              <button type="button" className="empire-cmd-cell" onClick={() => setDiplomacyOpen((v) => !v)}>
                Diplomacy
              </button>
              {diplomacyOpen
                ? (room.seats || [])
                    .filter((s) => s.team !== room.your_team)
                    .map((s) => (
                      <span key={s.team} className="contents">
                        {(["ally", "neutral", "enemy"] as const).map((st) => (
                          <button
                            key={s.team + st}
                            type="button"
                            className="empire-cmd-cell"
                            onClick={() => {
                              void sendCmd({ type: "diplomacy", other: s.team, stance: st });
                              setCmdMsg(`${s.name || s.team} is ${st}`);
                            }}
                          >
                            {s.label || s.name || s.team} {st}
                          </button>
                        ))}
                      </span>
                    ))
                : null}
              <button id="empire-town-center" type="button" className="empire-cmd-cell" title="Town hall (H)" onClick={() => selectTownCenter()}>
                Town Hall
              </button>
              <button
                id="empire-center-selection"
                type="button"
                className="empire-cmd-cell"
                title="Center on selection (space)"
                onClick={() => {
                  const id = selectedRef.current[0];
                  const e = room.game?.entities.find((en) => en.id === id);
                  if (e) panTo(e.x, e.z);
                }}
              >
                Center
              </button>
              {(() => {
                const mine = room.your_team;
                const groups = room.game?.alliances || {};
                const ally = mine
                  ? Object.keys(groups).find((t) => t !== mine && groups[t] === groups[mine])
                  : "";
                if (!ally || !matchActive) return null;
                const send = (good: "food" | "wood" | "gold" | "stone") => {
                  void sendCmd({ type: "tribute", to: ally, good, amount: 100 });
                  setCmdMsg(`Tribute 100 ${good}`);
                };
                return (
                  <>
                    <button type="button" className="empire-cmd-cell" onClick={() => send("food")}>Gift 100 Food</button>
                    <button type="button" className="empire-cmd-cell" onClick={() => send("wood")}>Gift 100 Wood</button>
                    <button type="button" className="empire-cmd-cell" onClick={() => send("gold")}>Gift 100 Gold</button>
                    <button type="button" className="empire-cmd-cell" onClick={() => send("stone")}>Gift 100 Stone</button>
                  </>
                );
              })()}
              <button
                id="empire-delete-unit"
                type="button"
                style={{ display: "none" }}
                onClick={() => {
                  const id = selectedRef.current[0];
                  const e = room.game?.entities.find((en) => en.id === id);
                  if (!e || e.team !== room.your_team || e.kind === "town_hall") return;
                  if (e.kind === "wall" || e.kind === "gate" || e.kind === "palisade" || e.kind === "fortified_wall") return;
                  void sendCmd({ type: "delete", targetId: id });
                  setCmdMsg("Deleted");
                }}
              />
              <button
                type="button"
                className="empire-cmd-cell"
                title="Select all units"
                onClick={() => selectAllUnits()}
              >
                Select All
              </button>
            </div>
            </div>
          </div>

                    {buildMode ? (
            <p className="mt-1 text-[12px] text-[#e0b050]">
              Build: {BUILD_LABELS[buildMode]}
              {buildMode === "dock"
                ? " — click the shore (or the water beside it) to place. Villagers walk over and build."
                : buildMode === "palisade" ||
                    buildMode === "wall" ||
                    buildMode === "fortified_wall"
                  ? " — click to start, click to place that segment and keep chaining along the hill. Right-click or Esc ends. G drops a gate on the line."
                  : buildMode === "gate"
                    ? " — click to place one gate. It opens for your units. Delete removes it."
                    : " — click the map to place. Villagers walk over and build. Esc cancels."}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
